import { useState } from 'react';
import { Box, Typography, IconButton, Skeleton, Tooltip } from '@mui/material';
import { PencilSimple, Plus } from '@phosphor-icons/react';
import { useUser } from '../../hooks/useUser';
import { useAlert } from '../../hooks/useAlert';
import StoreHoursDrawer from '../UserJourny/FirstUserJourney/StoreHoursDrawer';
import { useStores } from '../UserJourny/FirstUserJourney/useStores';
import MessagePopup from '../common/MessagePopup';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import NoDataFallback from '../common/NoDataFallback';

const formatTime = (timeString: string) => {
  if (!timeString) return '';

  // Convert 24-hour format to 12-hour format with AM/PM
  const [hours, minutes] = timeString.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12; // Convert 0 to 12
  return `${hour12}:${minutes} ${ampm}`;
};

const OpeningHours = () => {
  const { user } = useUser();
  const { alert, showAlert, setAlert } = useAlert();
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const [openHoursDrawer, setOpenHoursDrawer] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // Update useStores to use pagination parameters
  const {
    storesData,
    storesList,
    storesListLoading,
    isStoresListError,
    storesListError,
  } = useStores(paginationModel.page, paginationModel.pageSize);

  // Extract total count for server-side pagination
  const totalCount = storesData?.data?.total || 0;

  const days = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const handleOpenHours = (storeId: number) => {
    setSelectedStoreId(storeId);
    setOpenHoursDrawer(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'friendly_name',
      headerName: 'Outlet Name',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      align: 'left' as const,
      headerAlign: 'left' as const,
    },
    ...days.map(day => ({
      field: day.toLowerCase(),
      headerName: day,
      flex: 1,
      headerClassName: 'data-grid-header',
      align: 'left' as const,
      headerAlign: 'left' as const,
      type: 'string' as const,
      renderCell: (params: GridRenderCellParams) => {
        const storeHoursData = params.row.opening_hours || [];
        const dayData = storeHoursData.find((d: any) => d.day_of_week === day);

        return dayData?.is_open && dayData?.time_slots?.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {dayData.time_slots.map((slot: any, slotIndex: number) => (
              <Typography
                key={slotIndex}
                variant="body2"
                sx={{ whiteSpace: 'nowrap', fontSize: '12px' }}
              >
                {formatTime(slot.from_time)} - {formatTime(slot.to_time)}
                {slotIndex < dayData.time_slots.length - 1 && ', '}
              </Typography>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '12px' }}>Closed</Typography>
        );
      },
    })),
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.8,
      headerClassName: 'data-grid-header',
      headerAlign: 'right' as const,
      align: 'right' as const,
      renderCell: (params: GridRenderCellParams) => {
        const hasHours = params.row.opening_hours?.some(
          (day: any) =>
            day.is_open &&
            Array.isArray(day.time_slots) &&
            day.time_slots.length > 0
        );

        return (
          <IconButton
            onClick={() => handleOpenHours(params.row.id)}
            size="small"
            color="primary"
          >
            {hasHours ? (
              <Tooltip title="Edit Hours">
                <PencilSimple size={20} />
              </Tooltip>
            ) : (
              <Tooltip title="Add Hours">
                <Plus size={20} />
              </Tooltip>
            )}
          </IconButton>
        );
      },
    },
  ];

  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%', height: '100%' }}>
      {[...Array(5)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr',
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            alignItems: 'center',
          }}
        >
          <Skeleton variant="text" width="80%" height={24} />
          {days.map((_, dayIndex) => (
            <Skeleton key={dayIndex} variant="text" width="60%" height={24} />
          ))}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  if (isStoresListError && storesListError && storesListLoading) {
    return <NoDataFallback httpStatusCode={(storesListError as any)?.status} />;
  }

  if ((!storesList || storesList?.length === 0) && !storesListLoading) {
    return (
      <NoDataFallback
        message="No stores available"
        description="Looks like there are no stores to display right now. Once you create a store, it'll show up here"
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          border: '1px solid rgba(224, 224, 224, 1)',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {storesListLoading ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                bgcolor: '#F8F9FC',
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 0.8fr',
                gap: 2,
                p: 2,
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Outlet Name
              </Typography>
              {days.map(day => (
                <Typography
                  key={day}
                  sx={{
                    color: 'custom.textBlue',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {day}
                </Typography>
              ))}
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                  textAlign: 'right',
                }}
              >
                Action
              </Typography>
            </Box>
            <LoadingSkeleton />
          </Box>
        ) : (
          <DataGrid
            rows={storesList || []}
            columns={columns}
            loading={false}
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 75, 100]}
            // Add server-side pagination support like in Stores.tsx
            paginationMode="server"
            rowCount={totalCount}
            sx={{
              flex: 1,
              '& .data-grid-header': {
                bgcolor: '#F8F9FC',
                color: 'custom.textBlue',
                fontSize: '14px',
                fontWeight: 600,
                padding: '12px 16px',
              },
              '& .MuiDataGrid-cell': {
                padding: '12px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                '&:focus, &:focus-within': {
                  outline: 'none !important',
                },
              },
              border: 'none',
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none !important',
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  No stores available
                </Box>
              ),
            }}
          />
        )}
      </Box>

      <StoreHoursDrawer
        open={openHoursDrawer}
        onClose={() => setOpenHoursDrawer(false)}
        selectedStoreId={selectedStoreId}
        tenantName={user?.tenantName || ''}
        showAlert={(message: string, severity: 'success' | 'error') =>
          showAlert(message, severity)
        }
        existingHours={{
          store: { id: selectedStoreId },
          opening_hours:
            storesList?.find((store: any) => store.id === selectedStoreId)
              ?.opening_hours || [],
        }}
      />

      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default OpeningHours;
